/*
 * Webmail: sign-in, then the mailbox. Several mailboxes can be signed in at
 * once; the URL says which one is open (?mb=), and the panel's "Open webmail"
 * link names one with ?address=.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/icons";
import { ApiError, setSignedOutHandler, wmApi } from "./api";
import { MailApp } from "./MailApp";
import { SignIn } from "./SignIn";
import { BrandMark, CenteredCard } from "./parts";
import type { Account, SessionInfo } from "./types";

export function App() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const session = useQuery<SessionInfo | null>({
    queryKey: ["wm", "session"],
    queryFn: async () => {
      try {
        return await wmApi.get<SessionInfo>("/session");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
  // "Sign in to another mailbox", or the panel asked for one this session doesn't have yet.
  const [adding, setAdding] = useState<{ address: string | null } | null>(null);
  // A mailbox whose password the mail server stopped accepting: sign in to it again.
  const [reauth, setReauth] = useState<string | null>(null);

  const accounts = session.data?.accounts ?? [];
  const wanted = params.get("address")?.trim().toLowerCase() || null;
  // The panel's handoff lands on `?mailbox=<id>`: that's the mailbox asked for, ahead of any other.
  const handedOff = params.get("mailbox");
  const mb = handedOff ?? params.get("mb");
  const active: Account | null = accounts.find((a) => a.mailboxId === mb) ?? null;

  useEffect(() => {
    setSignedOutHandler((mailboxId) => {
      void qc.invalidateQueries({ queryKey: ["wm", "session"] });
      if (mailboxId) {
        const acct = qc.getQueryData<SessionInfo | null>(["wm", "session"])?.accounts.find((a) => a.mailboxId === mailboxId);
        if (acct) setReauth(acct.address);
      }
    });
  }, [qc]);

  // Decide which mailbox to show. Never quietly show a different mailbox from the one asked for.
  useEffect(() => {
    if (!session.isSuccess || !accounts.length) return;
    if (handedOff) {
      const next = new URLSearchParams(params);
      next.delete("mailbox");
      next.set("mb", handedOff);
      setParams(next, { replace: true });
      return;
    }
    if (wanted) {
      const match = accounts.find((a) => a.address === wanted);
      const next = new URLSearchParams(params);
      next.delete("address");
      if (match) {
        next.set("mb", match.mailboxId);
        setParams(next, { replace: true });
      } else {
        setAdding({ address: wanted });
        setParams(next, { replace: true });
      }
      return;
    }
    if (!active) {
      const next = new URLSearchParams(params);
      next.set("mb", accounts[0]!.mailboxId);
      setParams(next, { replace: true });
    }
  }, [session.isSuccess, accounts, handedOff, wanted, active, params, setParams]);

  const signedIn = (account: Account, all: Account[]) => {
    qc.setQueryData<SessionInfo | null>(["wm", "session"], (old) => ({ accounts: all, expiresAt: old?.expiresAt ?? "" }));
    void qc.invalidateQueries({ queryKey: ["wm", "session"] });
    void qc.invalidateQueries({ queryKey: ["wm", account.mailboxId] });
    setAdding(null);
    setReauth(null);
    setParams(new URLSearchParams({ mb: account.mailboxId }));
  };

  const title = useMemo(() => (active ? null : "Webmail"), [active]);
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  if (session.isPending) {
    return (
      <div className="flex h-dvh items-center justify-center bg-canvas text-dim" role="status" aria-label="Loading webmail">
        <Spinner size={22} />
      </div>
    );
  }
  if (session.isError) {
    return (
      <CenteredCard>
        <BrandMark />
        <h1 className="mt-4 text-[17px] font-semibold text-fg">Webmail isn't answering</h1>
        <p className="mt-1.5 text-[13px] text-body">{(session.error as Error).message}</p>
        <Button className="mt-4" variant="primary" onClick={() => void session.refetch()}>
          Try again
        </Button>
      </CenteredCard>
    );
  }
  if (!accounts.length) return <SignIn initialAddress={wanted} onSignedIn={signedIn} />;
  if (adding || reauth) {
    const address = reauth ?? adding?.address ?? null;
    return (
      <SignIn
        initialAddress={address}
        mode={reauth ? "again" : "add"}
        signedInAs={active?.address ?? accounts[0]!.address}
        onSignedIn={signedIn}
        onCancel={() => {
          setAdding(null);
          setReauth(null);
        }}
      />
    );
  }
  if (!active) return null; // the effect above picks one
  if (active.problem) {
    return (
      <CenteredCard>
        <BrandMark />
        <h1 className="mt-4 text-[17px] font-semibold text-fg">{active.address}</h1>
        <p className="mt-1.5 text-[13px] leading-relaxed text-body">{active.problem}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          {accounts.filter((a) => a.mailboxId !== active.mailboxId && !a.problem).map((a) => (
            <Button key={a.mailboxId} size="sm" onClick={() => setParams(new URLSearchParams({ mb: a.mailboxId }))}>
              Open {a.address}
            </Button>
          ))}
          <Button
            size="sm"
            variant="danger-soft"
            onClick={async () => {
              await wmApi.del(`/session/${active.mailboxId}`);
              await qc.invalidateQueries({ queryKey: ["wm", "session"] });
            }}
          >
            Sign out of this mailbox
          </Button>
        </div>
      </CenteredCard>
    );
  }
  return <MailApp key={active.mailboxId} account={active} accounts={accounts} onAddAccount={() => setAdding({ address: null })} />;
}
